import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import toast from 'react-hot-toast';
import {
  getWhatsappThread,
  replyWhatsappThread,
  setWhatsappThreadAgent,
} from '../../services/whatsappApi';
import {
  PaperAirplaneIcon,
  CheckIcon,
  ExclamationTriangleIcon,
  NoSymbolIcon,
} from '@heroicons/react/24/outline';
import DeptTag from './DeptTag';

const THREAD_POLL_MS = 8000;

function formatTime(tsMs, fallback = '') {
  if (!tsMs) return fallback;
  const n = typeof tsMs === 'string' ? parseInt(tsMs, 10) : tsMs;
  if (!n || Number.isNaN(n)) return fallback;
  const dt = new Date(n);
  if (Number.isNaN(dt.getTime())) return fallback;
  return dt.toLocaleString();
}

function formatShortTime(tsMs) {
  if (!tsMs) return '';
  const n = typeof tsMs === 'string' ? parseInt(tsMs, 10) : tsMs;
  if (!n || Number.isNaN(n)) return '';
  const dt = new Date(n);
  if (Number.isNaN(dt.getTime())) return '';
  return dt.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
}

// WA-TAB-24H-PICKER-oct19: render a template body with its positional {{n}} variables
// (1-based, named by `bindings`) filled from `values`; a binding with no value keeps its
// placeholder so the operator sees what is still missing before "Enviar plantilla".
export function renderTemplatePreview(preview, bindings, values) {
  let text = String(preview || '');
  (bindings || []).forEach((name, i) => {
    const val = String((values || {})[name] || '').trim();
    if (val) text = text.split(`{{${i + 1}}}`).join(val);
  });
  return text;
}

// Positional vars payload ({"1": ..., "2": ...}) for the reply API from the same bindings.
export function templateVarsPayload(bindings, values) {
  const out = {};
  (bindings || []).forEach((name, i) => {
    const val = String((values || {})[name] || '').trim();
    if (val) out[String(i + 1)] = val;
  });
  return out;
}

// Status tick rendering for outbound messages.
function StatusTicks({ status, t }) {
  if (!status) return null;
  if (status === 'failed') {
    return (
      <span className="inline-flex items-center text-red-500" title={t('whatsapp.status.failed')}>
        <ExclamationTriangleIcon className="h-3.5 w-3.5" />
      </span>
    );
  }
  if (status === 'read') {
    return (
      <span className="inline-flex text-sky-500" title={t('whatsapp.status.read')}>
        <CheckIcon className="h-3.5 w-3.5 -mr-2" />
        <CheckIcon className="h-3.5 w-3.5" />
      </span>
    );
  }
  if (status === 'delivered') {
    return (
      <span className="inline-flex text-gray-400" title={t('whatsapp.status.delivered')}>
        <CheckIcon className="h-3.5 w-3.5 -mr-2" />
        <CheckIcon className="h-3.5 w-3.5" />
      </span>
    );
  }
  if (status === 'sent') {
    return (
      <span className="inline-flex text-gray-400" title={t('whatsapp.status.sent')}>
        <CheckIcon className="h-3.5 w-3.5" />
      </span>
    );
  }
  // queued
  return (
    <span className="text-[10px] text-gray-400" title={t('whatsapp.status.queued')}>
      {t('whatsapp.status.queued')}
    </span>
  );
}

/**
 * Self-contained WhatsApp conversation panel for a single lead phone.
 *
 * Self-manages meta/messages, lazy-fetches the thread on mount (keyed on
 * leadPhone with a race-guard), and polls while mounted. Treats an HTTP 404 as
 * the "no conversation yet" empty state, not an error. When `readOnly` is set
 * the composer is hidden, so it can be embedded in read-only contexts (e.g. the
 * campaign call drawer) without duplicating the thread-rendering logic.
 */
function WhatsAppThreadPanel({
  leadPhone,
  department = '',
  campaignId = '',
  readOnly = false,
  className = '',
  // WA-TAB-24H-PICKER-oct19: values the embedding page already knows for the template
  // variables (cliente, asesor, sucursal, unidad, horario...). They overlay the server's
  // `vars_default` so the preview shows the exact text that will be sent.
  templateVars = null,
}) {
  const { t } = useTranslation();

  // WA-DEPARTMENT-CONTEXT-SPLIT-aug25: a single phone can have TWO distinct
  // conversations (ventas vs postventa). ``department`` selects which partition
  // this panel loads/replies to. Empty string keeps the legacy behavior (the
  // backend defaults to ventas), so untouched callers are unaffected. The
  // postventa drawer passes department="postventa" so it shows ONLY that thread.
  const dept = (department || '').trim();

  const [meta, setMeta] = useState(null);
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(false);
  // True once the first fetch for this thread settled (data or 404): the composer waits for it
  // so it never shows a "window closed / never opened" state before the meta is known.
  const [loaded, setLoaded] = useState(false);
  const [draft, setDraft] = useState('');
  const [sending, setSending] = useState(false);
  // Selected out-of-window template SID ('' = first offered).
  const [selectedTemplateSid, setSelectedTemplateSid] = useState('');
  // Per-thread AI toggle: null until the server value is known. Optimistic
  // updates set it locally; the 8s poll reconciles it against meta below.
  const [agentEnabled, setAgentEnabled] = useState(null);
  const [togglingAgent, setTogglingAgent] = useState(false);
  // Guard: while a toggle PUT is in-flight, don't let the poll clobber it.
  const agentPendingRef = useRef(false);

  const messagesEndRef = useRef(null);
  // The scrollable messages container. We read its scroll position to decide
  // whether an incoming poll is allowed to move the viewport.
  const messagesContainerRef = useRef(null);
  // WA-SCROLL-JUMP-sep8: `loadThread` (the 8s poll, the read-status reconcile,
  // and the post-send refresh) installs a BRAND-NEW messages array on every
  // call, even when the content is byte-identical or only a delivery-status tick
  // changed. A naive `scrollIntoView` effect keyed on `messages` therefore fired
  // on every poll and yanked the viewport back to the bottom while the operator
  // was scrolled UP reading history — the "scroll jumps" bug. We now autoscroll
  // ONLY when (a) the operator is already pinned at/near the bottom AND (b) a
  // genuinely NEW message was appended (not an identical poll or a status-only
  // change). Otherwise the viewport anchor is preserved so history stays put.
  // How far from the exact bottom still counts as "at bottom" (px). Covers
  // sub-pixel rounding and a just-arrived short bubble without stranding the
  // user mid-history.
  const NEAR_BOTTOM_PX = 120;
  const computeAtBottom = useCallback((el) => {
    if (!el) return true; // before first paint, default to bottom (initial load)
    const distanceFromBottom =
      el.scrollHeight - el.scrollTop - el.clientHeight;
    return distanceFromBottom <= NEAR_BOTTOM_PX;
  }, []);
  // Signature of the last rendered message so we can tell an appended message
  // apart from an identical poll or a pure status-tick update. `count` alone is
  // not enough (a resend/replace can keep the count); include the last ts.
  const lastMsgSigRef = useRef({ count: 0, lastTs: null });
  // Whether the operator is currently pinned to the bottom. Updated on every
  // manual scroll (see onMessagesScroll) so it reflects the operator's intent
  // and is NOT corrupted by the DOM growing when a new bubble is appended.
  const atBottomRef = useRef(true);
  const onMessagesScroll = useCallback(
    (e) => {
      atBottomRef.current = computeAtBottom(e.currentTarget);
    },
    [computeAtBottom]
  );

  // Race-guard: only the latest (leadPhone, department) fetch is allowed to
  // commit state. Both are part of the thread identity now.
  const leadPhoneRef = useRef(leadPhone);
  leadPhoneRef.current = leadPhone;
  const deptRef = useRef(dept);
  deptRef.current = dept;

  const loadThread = useCallback(
    async (phone, { showSpinner = false } = {}) => {
      if (!phone) return;
      if (showSpinner) setLoading(true);
      try {
        const params = { limit: 100 };
        if (dept) params.department = dept;
        const res = await getWhatsappThread(phone, params, campaignId);
        // user/drawer switched phone OR department mid-fetch
        if (leadPhoneRef.current !== phone || deptRef.current !== dept) return;
        setMeta(res?.data?.meta || null);
        setMessages(res?.data?.messages || []);
      } catch (e) {
        if (leadPhoneRef.current !== phone || deptRef.current !== dept) return;
        // 404 = no conversation yet for this lead → treat as empty, not an error.
        if (e?.response?.status === 404) {
          setMeta(null);
          setMessages([]);
        } else if (showSpinner) {
          toast.error(t('whatsapp.errors.loadThread'));
        }
      } finally {
        if (showSpinner && leadPhoneRef.current === phone) {
          setLoading(false);
          setLoaded(true);
        }
      }
    },
    [t, campaignId, dept]
  );

  // Lazy-load on mount / leadPhone or department change, then poll while mounted.
  useEffect(() => {
    if (!leadPhone) {
      setMeta(null);
      setMessages([]);
      return undefined;
    }
    setMeta(null);
    setMessages([]);
    setAgentEnabled(null);
    setDraft('');
    setSelectedTemplateSid('');
    setLoaded(false);
    // New thread identity: reset scroll bookkeeping so the first load of this
    // conversation snaps to the bottom and prior at-bottom state can't leak in.
    lastMsgSigRef.current = { count: 0, lastTs: null };
    atBottomRef.current = true;
    loadThread(leadPhone, { showSpinner: true });
    const id = setInterval(() => loadThread(leadPhone), THREAD_POLL_MS);
    return () => clearInterval(id);
  }, [leadPhone, dept, loadThread]);

  // Auto-scroll to the newest message — but ONLY when a genuinely new message
  // was appended AND the operator was pinned at/near the bottom. Identical polls
  // and status-only updates leave the viewport exactly where the operator put
  // it, so reading history is never interrupted. On a real chat switch the
  // signature resets (see the leadPhone/dept effect) so the first load snaps to
  // the bottom as expected.
  useEffect(() => {
    const count = messages.length;
    const lastTs = count ? messages[count - 1]?.ts ?? null : null;
    const prev = lastMsgSigRef.current;
    const appended =
      count > prev.count || (count > 0 && lastTs !== prev.lastTs && count === prev.count);
    const isFirstLoad = prev.count === 0 && count > 0;
    lastMsgSigRef.current = { count, lastTs };

    if (!count) return;
    // First load of a thread always lands at the bottom; afterwards only follow
    // when the operator was already at the bottom and a new message came in.
    if (isFirstLoad || (appended && atBottomRef.current)) {
      // 'auto' (not 'smooth'): a smooth animation launched every time a message
      // lands fights the operator's own scrolling and gets cancelled by the
      // next reflow; an instant jump-to-bottom is what a chat should do.
      messagesEndRef.current?.scrollIntoView({ behavior: 'auto' });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [messages]);

  // Reconcile the AI toggle against each poll unless a PUT is in flight.
  useEffect(() => {
    if (agentPendingRef.current) return;
    if (meta) {
      setAgentEnabled(typeof meta.agent_enabled === 'boolean' ? meta.agent_enabled : true);
    }
  }, [meta]);

  const handleToggleAgent = useCallback(async () => {
    if (readOnly || !leadPhone || togglingAgent) return;
    const next = !(agentEnabled ?? true);
    setTogglingAgent(true);
    agentPendingRef.current = true;
    setAgentEnabled(next); // optimistic
    try {
      await setWhatsappThreadAgent(leadPhone, next, campaignId, dept);
    } catch (e) {
      setAgentEnabled(!next); // revert on failure
      toast.error(t('whatsapp.errors.toggleAgent'));
    } finally {
      agentPendingRef.current = false;
      setTogglingAgent(false);
    }
  }, [readOnly, leadPhone, campaignId, dept, togglingAgent, agentEnabled, t]);

  const windowOpen = !!meta?.window_open;
  const optedOut = !!meta?.opted_out;
  const fallbackTemplateSid = meta?.fallback_template_sid || '';
  // Oct 18 (Roger 23:34): the picker shows the template NAME + a preview of its text; the
  // HX… SID is only a tooltip. An older API without the descriptor gets a generic name.
  const fallbackTemplateName = (meta?.fallback_template_name || '').trim() || t('whatsapp.templateFallbackName');
  const fallbackTemplatePreview = (meta?.fallback_template_preview || '').trim();
  const aiOn = agentEnabled ?? true;

  // WA-TAB-24H-PICKER-oct19 item 2: the ordered list of the tenant's APPROVED templates
  // (seguimiento v2 first for a callback request, then no contestó, hold/final when a cita
  // exists). An older API without `templates` degrades to the single fallback template.
  const templates = useMemo(() => {
    const list = Array.isArray(meta?.templates) ? meta.templates.filter((x) => x && x.template_sid) : [];
    if (list.length) return list;
    if (!fallbackTemplateSid) return [];
    return [{
      kind: 'fallback',
      label: fallbackTemplateName,
      name: meta?.fallback_template_name || '',
      template_sid: fallbackTemplateSid,
      preview: fallbackTemplatePreview,
      bindings: [],
      vars_default: {},
    }];
  }, [meta, fallbackTemplateSid, fallbackTemplateName, fallbackTemplatePreview]);
  const selectedTemplate = useMemo(
    () => templates.find((x) => x.template_sid === selectedTemplateSid) || templates[0] || null,
    [templates, selectedTemplateSid]
  );
  // Variables: server defaults (cliente from the thread, cita fields) overlaid by what the
  // embedding page knows (request row: nombre, asesor, sucursal, unidad, horario).
  const knownTemplateValues = useMemo(() => {
    const base = { ...(selectedTemplate?.vars_default || {}) };
    const lead = (meta?.lead_name || '').trim();
    if (lead) {
      if (!base.cliente) base.cliente = lead;
      if (!base.name) base.name = lead;
    }
    Object.entries(templateVars || {}).forEach(([k, v]) => {
      if (v !== undefined && v !== null && String(v).trim()) base[k] = String(v).trim();
    });
    return base;
  }, [selectedTemplate, templateVars, meta]);
  // Oct 20 (td447, Llamadas pendientes -> WhatsApp tab, rows with vehicle ""): a variable the row
  // cannot fill (unidad, horario...) is typed by the advisor INLINE. `editableBindings` is what the
  // ROW lacks (stable while typing); `missingBindings` is what is STILL empty after the advisor's
  // input and blocks the send -- a raw "{{4}}" is never sent to a customer.
  const [manualVars, setManualVars] = useState({});
  useEffect(() => { setManualVars({}); }, [leadPhone]); // another customer: never carry typed values over
  const editableBindings = useMemo(
    () => (selectedTemplate?.bindings || []).filter((b) => !String(knownTemplateValues[b] || '').trim()),
    [selectedTemplate, knownTemplateValues]
  );
  const templateValues = useMemo(() => {
    const out = { ...knownTemplateValues };
    editableBindings.forEach((b) => {
      const typed = String(manualVars[b] || '').trim();
      if (typed) out[b] = typed;
    });
    return out;
  }, [knownTemplateValues, editableBindings, manualVars]);
  const renderedTemplate = selectedTemplate
    ? renderTemplatePreview(selectedTemplate.preview, selectedTemplate.bindings, templateValues)
    : '';
  const missingBindings = (selectedTemplate?.bindings || []).filter((b) => !String(templateValues[b] || '').trim());
  const templateVarLabel = (name) => t(`whatsapp.templateVarLabels.${name}`, { defaultValue: name });

  // Item 1: when the window closed / reopens. The server derives both from the customer's
  // LAST INBOUND message (never our outbound), the same stamp the send gate uses.
  const lastInboundAt = meta?.last_inbound_at || null;
  const windowClosedAt = meta?.window_closed_at || null;

  const canSendText = windowOpen && !optedOut;
  const canSendTemplate = !windowOpen && !optedOut && !!selectedTemplate?.template_sid && missingBindings.length === 0;

  const handleSend = useCallback(async () => {
    if (readOnly || !leadPhone || optedOut || sending) return;
    let body;
    if (windowOpen) {
      const text = draft.trim();
      if (!text) return;
      body = { text };
    } else {
      if (!selectedTemplate?.template_sid) {
        toast.error(t('whatsapp.errors.noTemplate'));
        return;
      }
      // Never send a literal {{n}} marker: every binding must have a value.
      const stillMissing = (selectedTemplate.bindings || []).filter((b) => !String(templateValues[b] || '').trim());
      if (stillMissing.length) {
        toast.error(t('whatsapp.errors.templateVarsMissing', { vars: stillMissing.join(', ') }));
        return;
      }
      body = { template_sid: selectedTemplate.template_sid };
      const vars = templateVarsPayload(selectedTemplate.bindings, templateValues);
      if (Object.keys(vars).length) body.vars = vars;
    }
    setSending(true);
    try {
      await replyWhatsappThread(leadPhone, body, campaignId, dept);
      setDraft('');
      await loadThread(leadPhone);
    } catch (e) {
      const status = e?.response?.status;
      const detail = e?.response?.data?.detail;
      if (status === 409) {
        toast.error(t('whatsapp.errors.optedOut'));
      } else if (status === 400) {
        toast.error(t('whatsapp.errors.outsideWindow'));
      } else {
        toast.error(detail || t('whatsapp.errors.sendFailed'));
      }
      loadThread(leadPhone);
    } finally {
      setSending(false);
    }
  }, [
    readOnly,
    leadPhone,
    campaignId,
    dept,
    optedOut,
    sending,
    windowOpen,
    draft,
    selectedTemplate,
    templateValues,
    loadThread,
    t,
  ]);

  const onComposerKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      if (canSendText) handleSend();
    }
  };

  if (!leadPhone) {
    return (
      <div className={`flex flex-1 items-center justify-center text-sm text-gray-500 dark:text-gray-400 ${className}`}>
        {t('whatsapp.empty.selectThread')}
      </div>
    );
  }

  return (
    <div className={`flex min-h-0 flex-1 flex-col ${className}`}>
      {/* Header */}
      <div className="flex items-center justify-between border-b border-gray-200 px-4 py-3 dark:border-gray-700">
        <div>
          <div className="flex items-center gap-2">
            <span className="text-sm font-semibold text-gray-900 dark:text-gray-100">
              {leadPhone}
            </span>
            {dept && <DeptTag department={dept} t={t} />}
          </div>
          <div className="text-xs text-gray-400" data-testid="wa-window-state">
            {optedOut
              ? t('whatsapp.optedOut')
              : windowOpen
              ? t('whatsapp.windowOpen', { time: formatTime(meta?.window_expires_at) })
              : windowClosedAt
              ? t('whatsapp.windowClosedAt', { time: formatTime(windowClosedAt) })
              : t('whatsapp.windowClosed')}
          </div>
        </div>
        {!readOnly && meta && (
          <button
            type="button"
            onClick={handleToggleAgent}
            disabled={togglingAgent}
            title={t('whatsapp.agentToggleHelp')}
            aria-pressed={aiOn}
            data-testid="wa-ai-toggle"
            className={`inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-xs font-medium transition-colors disabled:cursor-not-allowed disabled:opacity-60 ${
              aiOn
                ? 'bg-emerald-100 text-emerald-700 dark:bg-emerald-500/20 dark:text-emerald-300'
                : 'bg-gray-200 text-gray-600 dark:bg-gray-700 dark:text-gray-300'
            }`}
          >
            <span
              className={`h-2 w-2 rounded-full ${aiOn ? 'bg-emerald-500' : 'bg-gray-400'}`}
            />
            {/* What the state MEANS ("La IA responde automáticamente") + what the click DOES ("Pausar IA"). */}
            <span>{aiOn ? t('whatsapp.agentOnLabel') : t('whatsapp.agentOffLabel')}</span>
            <span className="opacity-70">·</span>
            <span className="underline decoration-dotted underline-offset-2">
              {aiOn ? t('whatsapp.agentTurnOff') : t('whatsapp.agentTurnOn')}
            </span>
          </button>
        )}
      </div>

      {/* Messages */}
      <div
        ref={messagesContainerRef}
        onScroll={onMessagesScroll}
        className="min-h-0 flex-1 overflow-y-auto px-4 py-4"
      >
        {loading ? (
          <div className="text-sm text-gray-500 dark:text-gray-400">
            {t('common.loading')}
          </div>
        ) : messages.length === 0 ? (
          <div className="text-sm text-gray-500 dark:text-gray-400">
            {t('whatsapp.empty.messages')}
          </div>
        ) : (
          messages.map((m, idx) => {
            const isOut = m.direction === 'out';
            // WA-TAB-24H-PICKER-oct19 item 5: the server says WHO sent it ('employee' |
            // 'automated'); a post-call template send carries the VOICE agent id, so the
            // old `!agent_id` guess called it "Manual". Legacy API (no sender_kind): a
            // template send is automated, else fall back to the agent_id guess.
            const isAuto = isOut && (m.sender_kind
              ? m.sender_kind !== 'employee'
              : !!m.template_used || !m.agent_id);
            return (
              <div
                key={`${m.ts}-${idx}`}
                className={`mb-2 flex ${isOut ? 'justify-end' : 'justify-start'}`}
              >
                <div
                  className={`max-w-[75%] rounded-2xl px-3 py-2 text-sm ${
                    isOut
                      ? 'bg-emerald-500 text-white'
                      : 'bg-gray-100 text-gray-900 dark:bg-gray-700 dark:text-gray-100'
                  }`}
                >
                  <div className="whitespace-pre-wrap break-words">{m.body}</div>
                  <div
                    className={`mt-1 flex items-center justify-end gap-1 text-[10px] ${
                      isOut ? 'text-emerald-100' : 'text-gray-400'
                    }`}
                  >
                    {isOut && (
                      <span className="mr-1" data-testid="wa-msg-origin">
                        {isAuto ? t('whatsapp.auto') : t('whatsapp.manual')}
                        {m.template_used ? ` · ${t('whatsapp.template')}` : ''}
                      </span>
                    )}
                    <span>{formatShortTime(m.ts)}</span>
                    {isOut && <StatusTicks status={m.status} t={t} />}
                  </div>
                </div>
              </div>
            );
          })
        )}
        <div ref={messagesEndRef} />
      </div>

      {/* Composer (hidden in read-only mode; waits for the first thread load) */}
      {!readOnly && loaded && (
        <div className="border-t border-gray-200 p-3 dark:border-gray-700">
          {optedOut ? (
            <div className="flex items-center justify-center gap-2 rounded-md bg-red-50 px-3 py-3 text-sm text-red-600 dark:bg-red-500/10 dark:text-red-400">
              <NoSymbolIcon className="h-4 w-4" />
              {t('whatsapp.leadOptedOut')}
            </div>
          ) : !windowOpen ? (
            <div className="space-y-2">
              <div className="flex items-center gap-2 text-xs text-amber-600 dark:text-amber-400">
                <ExclamationTriangleIcon className="h-4 w-4 flex-shrink-0" />
                {t('whatsapp.outsideWindowNotice')}
              </div>
              {/* Item 1: the rule in plain words + when the window closed (customer's last message). */}
              <p className="text-xs text-gray-600 dark:text-gray-300" data-testid="wa-window-explain">
                {t('whatsapp.outsideWindowExplain')}
              </p>
              <p className="text-[11px] text-gray-500 dark:text-gray-400" data-testid="wa-window-times">
                {lastInboundAt
                  ? t('whatsapp.windowTimes', {
                      last: formatTime(lastInboundAt),
                      closed: formatTime(windowClosedAt),
                    })
                  : t('whatsapp.windowNeverOpened')}
              </p>
              {templates.length ? (
                <div className="flex items-start gap-2">
                  <div
                    className="min-w-0 flex-1 rounded-md border border-gray-200 bg-gray-50 px-3 py-2 text-sm text-gray-600 dark:border-gray-600 dark:bg-gray-700/50 dark:text-gray-300"
                    title={t('whatsapp.templateSidTitle', { sid: selectedTemplate?.template_sid || '' })}
                    data-testid="wa-template-picker"
                  >
                    <label className="flex items-center gap-2 font-medium text-gray-800 dark:text-gray-100">
                      <span>{t('whatsapp.templateLabel')}: </span>
                      <select
                        value={selectedTemplate?.template_sid || ''}
                        onChange={(e) => setSelectedTemplateSid(e.target.value)}
                        data-testid="wa-template-select"
                        className="min-w-0 flex-1 rounded-md border border-gray-200 bg-white px-2 py-1 text-sm text-gray-900 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-100"
                      >
                        {templates.map((tpl) => (
                          <option key={tpl.template_sid} value={tpl.template_sid}>
                            {tpl.label || tpl.name || t('whatsapp.templateFallbackName')}
                            {tpl.name && tpl.label && tpl.name !== tpl.label ? ` (${tpl.name})` : ''}
                          </option>
                        ))}
                      </select>
                    </label>
                    {/* Item 2: the EXACT rendered text (variables filled from the request). */}
                    <div className="mt-1 whitespace-pre-wrap break-words text-xs text-gray-700 dark:text-gray-200" data-testid="wa-template-preview">
                      {renderedTemplate || t('whatsapp.templatePreviewMissing')}
                    </div>
                    {missingBindings.length > 0 && (
                      <div className="mt-1 text-[11px] text-amber-600 dark:text-amber-400" data-testid="wa-template-missing">
                        {t('whatsapp.templateMissingVars', { vars: missingBindings.join(', ') })}
                      </div>
                    )}
                    {editableBindings.length > 0 && (
                      <div className="mt-2 grid gap-1.5" data-testid="wa-template-vars-form">
                        {editableBindings.map((name) => (
                          <label key={name} className="flex items-center gap-2 text-xs text-gray-700 dark:text-gray-200">
                            <span className="w-20 flex-shrink-0 font-medium">{templateVarLabel(name)}</span>
                            <input
                              type="text"
                              value={manualVars[name] || ''}
                              onChange={(e) => setManualVars((prev) => ({ ...prev, [name]: e.target.value }))}
                              placeholder={t('whatsapp.templateVarPlaceholder', { name: templateVarLabel(name) })}
                              data-testid={`wa-template-var-${name}`}
                              className="min-w-0 flex-1 rounded-md border border-amber-300 bg-white px-2 py-1 text-xs text-gray-900 focus:border-emerald-500 focus:outline-none dark:border-amber-500/60 dark:bg-gray-700 dark:text-gray-100"
                            />
                          </label>
                        ))}
                      </div>
                    )}
                  </div>
                  <button
                    type="button"
                    disabled={!canSendTemplate || sending}
                    onClick={handleSend}
                    data-testid="wa-send-template"
                    title={missingBindings.length ? t('whatsapp.templateMissingVars', { vars: missingBindings.join(', ') }) : undefined}
                    className="inline-flex items-center gap-1 rounded-md bg-emerald-500 px-3 py-2 text-sm font-medium text-white hover:bg-emerald-600 disabled:cursor-not-allowed disabled:opacity-50"
                  >
                    <PaperAirplaneIcon className="h-4 w-4" />
                    {t('whatsapp.sendTemplate')}
                  </button>
                </div>
              ) : (
                <div className="rounded-md bg-gray-50 px-3 py-2 text-xs text-gray-500 dark:bg-gray-700/50 dark:text-gray-400">
                  {t('whatsapp.noTemplateConfigured')}
                </div>
              )}
            </div>
          ) : (
            <div className="flex items-end gap-2">
              <textarea
                value={draft}
                onChange={(e) => setDraft(e.target.value)}
                onKeyDown={onComposerKeyDown}
                rows={1}
                data-testid="wa-composer"
                placeholder={t('whatsapp.composerPlaceholder')}
                className="flex-1 resize-none rounded-md border border-gray-200 bg-white px-3 py-2 text-sm text-gray-900 focus:border-emerald-500 focus:outline-none dark:border-gray-600 dark:bg-gray-700 dark:text-gray-100"
              />
              <button
                type="button"
                disabled={!canSendText || sending || !draft.trim()}
                onClick={handleSend}
                className="inline-flex items-center gap-1 rounded-md bg-emerald-500 px-3 py-2 text-sm font-medium text-white hover:bg-emerald-600 disabled:cursor-not-allowed disabled:opacity-50"
              >
                <PaperAirplaneIcon className="h-4 w-4" />
                {t('whatsapp.send')}
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default WhatsAppThreadPanel;
